/**
 * Starts the visit's UI dictionary download from index.html.
 *
 * main.tsx renders only once the current language's dictionary has loaded (no
 * flash of English), and every language but English is a chunk of its own — so
 * a non-English visitor's first paint used to wait for the bundle, THEN for that
 * chunk. The build (vite.config.ts) inlines a map language → chunk URLs and a
 * tiny script that `<link rel="modulepreload">`s the chunk of the language the
 * visit will start in, while the bundle is still downloading; the app's own
 * `import()` then finds it already fetched.
 */

import { insertEarlyScript } from "../earlyScripts.js";

/** Where the UI language the visitor chose is remembered (store.ts). */
export const LANGUAGE_STORAGE_KEY = "lorevok:lang";

/**
 * The UI language a visit starts in: the saved choice when it is supported,
 * else the first supported browser language, else `fallback` — only the primary
 * subtag counts ("pt-BR" → "pt"). The one rule store.ts and the inline script
 * share. MUST stay self-contained (no imports, no outer variables):
 * vite.config.ts inlines its source text into index.html.
 */
export function initialLanguage(
  saved: string | null | undefined,
  browser: readonly string[],
  supported: readonly string[],
  fallback: string
): string {
  const pick = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    const primary = raw.trim().toLowerCase().split(/[-_]/)[0];
    return supported.indexOf(primary) >= 0 ? primary : null;
  };
  const chosen = pick(saved);
  if (chosen) return chosen;
  for (let i = 0; i < browser.length; i++) {
    const resolved = pick(browser[i]);
    if (resolved) return resolved;
  }
  return fallback;
}

/** The parts of a Rollup output file this reads (vite.config.ts passes the build's bundle). */
export interface BundleFile {
  type: string;
  fileName: string;
  facadeModuleId?: string | null;
  isDynamicEntry?: boolean;
  isEntry?: boolean;
  imports?: readonly string[];
}

/**
 * Language code → the files its dictionary chunk needs (the chunk itself, then
 * the chunks it imports; never the entry, which is loading anyway), as URLs
 * under `base`. A dictionary is `src/lib/i18n/<code>.ts`, loaded by `import()`
 * in dictionaries.ts; English is in the entry and has none.
 */
export function dictionaryChunkMap(bundle: Record<string, BundleFile>, base: string): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const file of Object.values(bundle)) {
    if (file.type !== "chunk" || !file.isDynamicEntry || !file.facadeModuleId) continue;
    const match = /[\\/]src[\\/]lib[\\/]i18n[\\/]([a-z]{2})\.ts$/.exec(file.facadeModuleId);
    if (!match) continue;
    const files: string[] = [];
    const visit = (fileName: string) => {
      const chunk = bundle[fileName];
      if (files.includes(fileName) || !chunk || chunk.isEntry) return;
      files.push(fileName);
      for (const dependency of chunk.imports ?? []) visit(dependency);
    };
    visit(file.fileName);
    map[match[1]] = files.map((fileName) => base + fileName);
  }
  return map;
}

/**
 * The inline `<script>` body vite.config.ts injects into index.html: picks the
 * visit's language the way store.ts will (`initialLanguage`) and modulepreloads
 * its chunks, `crossorigin` like Vite's own module preloads.
 */
export function dictionaryPreloadScript(
  chunks: Record<string, string[]>,
  supported: readonly string[],
  fallback: string
): string {
  const data = (value: unknown) => JSON.stringify(value).replace(/</g, "\\u003c");
  return (
    "(function(){try{var files=" + data(chunks) + ";var pick=" + initialLanguage.toString() + ";var saved=null;" +
    "try{saved=window.localStorage.getItem(" + data(LANGUAGE_STORAGE_KEY) + ");}catch(e){}" +
    "var lang=pick(saved,navigator.languages||[navigator.language]," + data(supported) + "," + data(fallback) + ");" +
    '(Object.prototype.hasOwnProperty.call(files,lang)?files[lang]:[]).forEach(function(href){var l=document.createElement("link");' +
    'l.setAttribute("rel","modulepreload");l.setAttribute("crossorigin","");l.setAttribute("href",href);document.head.appendChild(l);});' +
    "}catch(e){}})();"
  );
}

/**
 * index.html with the dictionary preload's script added — unchanged when the
 * bundle has no dictionary chunks. What vite.config.ts does once the bundle
 * exists (`bundle` is the build's output, `base` its public base path).
 */
export function withDictionaryPreload(
  html: string,
  bundle: Record<string, BundleFile>,
  base: string,
  supported: readonly string[],
  fallback: string
): string {
  const chunks = dictionaryChunkMap(bundle, base);
  if (Object.keys(chunks).length === 0) return html;
  return insertEarlyScript(html, dictionaryPreloadScript(chunks, supported, fallback));
}
